/**
 * Places Autocomplete Component (Shared)
 *
 * Reusable React autocomplete that uses Places API (New) via getPlacesService()
 * (AutocompleteSuggestion.fetchAutocompleteSuggestions + Place.fetchFields).
 *
 * Props:
 * @prop {Function} onPlaceSelect - Callback invoked when a place is selected from suggestions.
 * @prop {Function} [onCustomInput] - Callback invoked when a custom input is provided (if allowed).
 * @prop {Function} [onInputChange] - Callback invoked whenever the input value changes.
 * @prop {string} [placeholder='Search for a place...'] - Placeholder text for the input field.
 * @prop {string} [initialValue=''] - Initial text value for the input field.
 * @prop {string} [className=''] - Custom CSS class for the input field.
 * @prop {string} [placeType='address'] - Type of place to search for. Valid values:
 *   - 'address' (default): Street-level address results
 *   - '(cities)' or 'cities': City-level results only
 *   - 'geocode': No type restriction — shows cities, countries, and attractions
 *   Any unrecognised value also produces unrestricted results.
 * @prop {boolean} [allowCustomInput=false] - Whether to allow non-Google Place entries.
 * @prop {string[]} [countries=[]] - Array of country codes to restrict search (e.g., ['US', 'CA']).
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getPlacesService, initializePlacesService } from '../../services/placesService';
import './PlacesAutocomplete.css';

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_COUNTRIES = [];

function typesFromPlaceType(placeType) {
  if (placeType === '(cities)' || placeType === 'cities') {
    return ['(cities)'];
  }
  if (placeType === 'address') {
    return ['address'];
  }
  return [];
}

function detailsFromPrediction(prediction) {
  const description = prediction.description || '';
  const name = description.split(',')[0].trim();
  return {
    placeId: prediction.placeId || '',
    name,
    formattedAddress: description,
    addressComponents: {
      streetNumber: '',
      route: '',
      locality: name,
      administrativeArea: '',
      country: '',
      postalCode: ''
    },
    coordinates: { lat: 0, lng: 0 },
    phoneNumber: '',
    website: '',
    types: prediction.types || []
  };
}

export const PlacesAutocomplete = ({
  onPlaceSelect,
  onCustomInput,
  onInputChange,
  placeholder = 'Search for a place...',
  initialValue = '',
  className = '',
  placeType = 'address',
  allowCustomInput = false,
  countries = EMPTY_COUNTRIES
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);

  const inputRef = useRef(null);
  const prevInitialValue = useRef(initialValue);
  const placeJustSelected = useRef(false);
  const isSelecting = useRef(false);
  const skipNextSearch = useRef(true);

  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onCustomInputRef = useRef(onCustomInput);
  const onInputChangeRef = useRef(onInputChange);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    onCustomInputRef.current = onCustomInput;
  }, [onCustomInput]);

  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  useEffect(() => {
    let cancelled = false;

    const prepareService = async () => {
      try {
        let service;
        try {
          service = getPlacesService();
        } catch {
          service = initializePlacesService();
        }
        await service.initialize();
        if (!cancelled) {
          setIsLoading(false);
          setError(null);
        }
      } catch (err) {
        console.error('PlacesAutocomplete: Failed to initialize Places service:', err);
        if (!cancelled) {
          setError('Failed to initialize Google Places. Please try again.');
          setIsLoading(false);
        }
      }
    };

    prepareService();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (initialValue !== prevInitialValue.current) {
      skipNextSearch.current = true;
      setInputValue(initialValue);
      setPredictions([]);
      prevInitialValue.current = initialValue;
    }
  }, [initialValue]);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }

    const query = inputValue.trim();
    if (!query) {
      setPredictions([]);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const service = getPlacesService();
        const results = await service.getPlacePredictions(query, {
          types: typesFromPlaceType(placeType),
          countries,
        });
        if (!cancelled) {
          setPredictions(results);
          setHighlightedIndex(-1);
          setError(null);
        }
      } catch (err) {
        console.error('PlacesAutocomplete: Prediction search failed:', err);
        if (!cancelled) {
          setPredictions([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inputValue, placeType, countries]);

  useEffect(() => {
    if (predictions.length === 0 || !inputRef.current) {
      setMenuStyle(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      const rect = inputRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        right: 'auto',
        zIndex: 10000,
      });
    };

    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [predictions]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onInputChangeRef.current) {
      onInputChangeRef.current(newValue);
    }
  };

  const selectPrediction = async (prediction) => {
    if (!prediction?.placeId) {
      return;
    }

    placeJustSelected.current = true;
    isSelecting.current = true;
    skipNextSearch.current = true;
    setPredictions([]);
    setHighlightedIndex(-1);

    const preliminary = detailsFromPrediction(prediction);
    setInputValue(preliminary.formattedAddress);
    if (onInputChangeRef.current) {
      onInputChangeRef.current(preliminary.formattedAddress);
    }
    // City fields can use the prediction immediately so Save does not race fetchFields.
    // Address fields need real address components, so wait for details below.
    if (placeType !== 'address' && onPlaceSelectRef.current) {
      onPlaceSelectRef.current(preliminary);
    }

    try {
      const service = getPlacesService();
      const placeDetails = await service.getPlaceDetails(prediction.placeId);
      const displayValue = placeDetails.formattedAddress || placeDetails.name || prediction.description;

      skipNextSearch.current = true;
      setInputValue(displayValue);
      if (onInputChangeRef.current) {
        onInputChangeRef.current(displayValue);
      }
      if (onPlaceSelectRef.current) {
        onPlaceSelectRef.current(placeDetails);
      }
    } catch (err) {
      console.error('PlacesAutocomplete: Failed to get place details:', err);
      setError('Failed to get place details. Please try again.');
      if (placeType === 'address' && allowCustomInput && onCustomInputRef.current) {
        onCustomInputRef.current(prediction.description);
      }
    } finally {
      isSelecting.current = false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (predictions.length === 0) return;
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % predictions.length);
      return;
    }

    if (e.key === 'ArrowUp') {
      if (predictions.length === 0) return;
      e.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? predictions.length - 1 : prev - 1));
      return;
    }

    if (e.key === 'Escape') {
      setPredictions([]);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (predictions.length > 0) {
        const index = highlightedIndex >= 0 ? highlightedIndex : 0;
        selectPrediction(predictions[index]);
        return;
      }
      if (allowCustomInput && onCustomInputRef.current && inputRef.current) {
        onCustomInputRef.current(inputRef.current.value);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (
        !placeJustSelected.current &&
        !isSelecting.current &&
        allowCustomInput &&
        onCustomInputRef.current &&
        inputRef.current
      ) {
        const value = inputRef.current.value.trim();
        if (value) {
          onCustomInputRef.current(value);
        }
      }
      placeJustSelected.current = false;
      setPredictions([]);
    }, 200);
  };

  return (
    <div className={`places-autocomplete ${className}`}>
      <div className="places-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="places-input"
          disabled={isLoading && !inputValue}
          autoComplete="new-password"
        />

        {(isLoading || isSearching) && (
          <div className="places-loading-indicator">
            <div className="places-spinner"></div>
          </div>
        )}
      </div>

      {predictions.length > 0 && menuStyle && createPortal(
        <ul className="places-predictions-list" role="listbox" style={menuStyle}>
          {predictions.map((prediction, index) => (
            <li
              key={prediction.placeId || `${prediction.description}-${index}`}
              role="option"
              aria-selected={index === highlightedIndex}
              className={`places-prediction-item${index === highlightedIndex ? ' places-prediction-selected' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => selectPrediction(prediction)}
            >
              {prediction.description}
            </li>
          ))}
        </ul>,
        document.body
      )}

      {error && (
        <div className="places-error-message" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
